import React, { useEffect, useRef } from "react";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const HEX_RADIUS = 28;
const HEX_WIDTH = HEX_RADIUS * 2;
const HEX_HEIGHT = Math.sqrt(3) * HEX_RADIUS;
const COL_SPACING = HEX_WIDTH * 0.75;
const ROW_SPACING = HEX_HEIGHT;
const GLOW_RADIUS = 100;
const BASE_ALPHA = 0.04;
const MAX_GLOW_ALPHA = 0.2;

// Pulse constants
const MAX_PULSES = 12;
const PULSE_SPAWN_INTERVAL = 2000; // ms between spawns
const PULSE_SPEED = 0.05; // progress per frame (0-1 along an edge)
const PULSE_MAX_STEPS = 60; // max edges a pulse travels before dying
const PULSE_DOT_RADIUS = 2;
const AFTERGLOW_DURATION = 240; // frames for afterglow to fade
const VERTEX_EPSILON = 0.5; // tolerance for vertex position matching

interface LitEdge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  life: number;
}

interface Pulse {
  x1: number; // start vertex of current edge
  y1: number;
  x2: number; // end vertex of current edge
  y2: number;
  progress: number;
  stepsLeft: number;
}

function getHexCenter(col: number, row: number) {
  return {
    x: col * COL_SPACING,
    y: row * ROW_SPACING + (col % 2 === 1 ? ROW_SPACING / 2 : 0)
  };
}

function vtxMatch(ax: number, ay: number, bx: number, by: number) {
  return (
    Math.abs(ax - bx) < VERTEX_EPSILON && Math.abs(ay - by) < VERTEX_EPSILON
  );
}

// Find all vertices connected to (vx, vy) by a hex edge
function findConnectedVertices(
  vx: number,
  vy: number
): Array<{ x: number; y: number }> {
  const results: Array<{ x: number; y: number }> = [];
  const approxCol = Math.round(vx / COL_SPACING);
  const approxRow = Math.round(vy / ROW_SPACING);

  for (let dc = -2; dc <= 2; dc++) {
    for (let dr = -2; dr <= 2; dr++) {
      const col = approxCol + dc;
      const row = approxRow + dr;
      const center = getHexCenter(col, row);
      const verts = hexVertices(center.x, center.y);
      for (let i = 0; i < 6; i++) {
        if (vtxMatch(verts[i].x, verts[i].y, vx, vy)) {
          // Edges from vertex i go to vertex (i+1)%6 and vertex (i+5)%6
          const next = verts[(i + 1) % 6];
          const prev = verts[(i + 5) % 6];
          if (!results.some((r) => vtxMatch(r.x, r.y, next.x, next.y))) {
            results.push({ x: next.x, y: next.y });
          }
          if (!results.some((r) => vtxMatch(r.x, r.y, prev.x, prev.y))) {
            results.push({ x: prev.x, y: prev.y });
          }
        }
      }
    }
  }
  return results;
}

function spawnPulse(cols: number, rows: number): Pulse {
  const col = Math.floor(Math.random() * cols);
  const row = Math.floor(Math.random() * rows);
  const edgeIndex = Math.floor(Math.random() * 6);
  const center = getHexCenter(col, row);
  const verts = hexVertices(center.x, center.y);
  return {
    x1: verts[edgeIndex].x,
    y1: verts[edgeIndex].y,
    x2: verts[(edgeIndex + 1) % 6].x,
    y2: verts[(edgeIndex + 1) % 6].y,
    progress: 0,
    stepsLeft: PULSE_MAX_STEPS
  };
}

function spawnPulseAt(x: number, y: number): Pulse {
  // Find the nearest hex center to the click position
  const col = Math.round(x / COL_SPACING);
  const row = Math.round(y / ROW_SPACING);
  const center = getHexCenter(col, row);
  const verts = hexVertices(center.x, center.y);

  // Find the nearest vertex to the click
  let nearest = 0;
  let minDist = Infinity;
  for (let i = 0; i < 6; i++) {
    const d = Math.hypot(verts[i].x - x, verts[i].y - y);
    if (d < minDist) {
      minDist = d;
      nearest = i;
    }
  }

  return {
    x1: verts[nearest].x,
    y1: verts[nearest].y,
    x2: verts[(nearest + 1) % 6].x,
    y2: verts[(nearest + 1) % 6].y,
    progress: 0,
    stepsLeft: PULSE_MAX_STEPS
  };
}

function hexVertices(cx: number, cy: number) {
  const verts = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i;
    verts.push({
      x: cx + HEX_RADIUS * Math.cos(angle),
      y: cy + HEX_RADIUS * Math.sin(angle)
    });
  }
  return verts;
}

function distToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

export function HexBackground() {
  const theme = useTheme();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const hexColorRef = useRef(theme.palette.hexColor);

  // Keep hexColor in sync with theme changes
  useEffect(() => {
    hexColorRef.current = theme.palette.hexColor;
  }, [theme.palette.hexColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animFrame = 0;
    const pulses: Pulse[] = [];
    const litEdges: LitEdge[] = [];
    let lastSpawn = 0;

    function resize() {
      canvas!.width = window.innerWidth;
      canvas!.height = window.innerHeight;
      if (reducedMotion) draw(0);
    }

    function onMouseMove(e: MouseEvent) {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    }

    function onMouseLeave() {
      mouseRef.current = { x: -1000, y: -1000 };
    }

    function onClickOrTouch(x: number, y: number) {
      if (pulses.length < MAX_PULSES) {
        pulses.push(spawnPulseAt(x, y));
      }
    }

    function onClick(e: MouseEvent) {
      onClickOrTouch(e.clientX, e.clientY);
    }

    function onTouchStart(e: TouchEvent) {
      const touch = e.touches[0];
      if (touch) onClickOrTouch(touch.clientX, touch.clientY);
    }

    function updatePulses(cols: number, rows: number, now: number) {
      if (
        pulses.length < MAX_PULSES &&
        now - lastSpawn > PULSE_SPAWN_INTERVAL
      ) {
        pulses.push(spawnPulse(cols, rows));
        lastSpawn = now;
      }

      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        p.progress += PULSE_SPEED;

        if (p.progress >= 1) {
          // Edge fully traversed — add to afterglow
          litEdges.push({
            x1: p.x1,
            y1: p.y1,
            x2: p.x2,
            y2: p.y2,
            life: AFTERGLOW_DURATION
          });

          // The dot is now at (x2, y2). Find connected vertices and pick
          // one that isn't where we came from.
          const connected = findConnectedVertices(p.x2, p.y2);
          const candidates = connected.filter(
            (v) => !vtxMatch(v.x, v.y, p.x1, p.y1)
          );

          if (candidates.length === 0) {
            pulses.splice(i, 1);
            continue;
          }

          const next =
            candidates[Math.floor(Math.random() * candidates.length)];
          p.x1 = p.x2;
          p.y1 = p.y2;
          p.x2 = next.x;
          p.y2 = next.y;
          p.progress = 0;
          p.stepsLeft--;

          if (p.stepsLeft <= 0) {
            pulses.splice(i, 1);
          }
        }
      }

      // Decay afterglow edges
      for (let i = litEdges.length - 1; i >= 0; i--) {
        litEdges[i].life--;
        if (litEdges[i].life <= 0) litEdges.splice(i, 1);
      }
    }

    function drawPulses() {
      const hexColor = hexColorRef.current;

      // Draw afterglow edges
      for (const edge of litEdges) {
        const t = edge.life / AFTERGLOW_DURATION;
        const alpha = 0.5 * t * t;
        ctx!.beginPath();
        ctx!.moveTo(edge.x1, edge.y1);
        ctx!.lineTo(edge.x2, edge.y2);
        ctx!.strokeStyle = `rgba(${hexColor}, ${alpha})`;
        ctx!.lineWidth = 1 + 1.5 * t;
        ctx!.shadowColor = `rgba(${hexColor}, ${alpha * 0.6})`;
        ctx!.shadowBlur = 6 * t;
        ctx!.stroke();
      }

      // Draw active pulse: lit portion of current edge + glowing dot
      for (const p of pulses) {
        const dotX = p.x1 + (p.x2 - p.x1) * p.progress;
        const dotY = p.y1 + (p.y2 - p.y1) * p.progress;

        // Lit portion behind the dot
        ctx!.beginPath();
        ctx!.moveTo(p.x1, p.y1);
        ctx!.lineTo(dotX, dotY);
        ctx!.strokeStyle = `rgba(${hexColor}, 0.6)`;
        ctx!.lineWidth = 2;
        ctx!.shadowColor = `rgba(${hexColor}, 0.5)`;
        ctx!.shadowBlur = 8;
        ctx!.stroke();

        // Glowing dot
        ctx!.beginPath();
        ctx!.arc(dotX, dotY, PULSE_DOT_RADIUS, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(${hexColor}, 0.9)`;
        ctx!.shadowColor = `rgba(${hexColor}, 0.8)`;
        ctx!.shadowBlur = 16;
        ctx!.fill();
      }

      ctx!.shadowBlur = 0;
    }

    function draw(now: number) {
      ctx!.clearRect(0, 0, canvas!.width, canvas!.height);
      const mouse = mouseRef.current;
      const hexColor = hexColorRef.current;

      const cols = Math.ceil(canvas!.width / COL_SPACING) + 2;
      const rows = Math.ceil(canvas!.height / ROW_SPACING) + 2;

      for (let col = -1; col < cols; col++) {
        for (let row = -1; row < rows; row++) {
          const cx = col * COL_SPACING;
          const cy = row * ROW_SPACING + (col % 2 === 1 ? ROW_SPACING / 2 : 0);

          const distCenter = Math.hypot(cx - mouse.x, cy - mouse.y);
          const isNear = distCenter < GLOW_RADIUS + HEX_RADIUS;
          const verts = hexVertices(cx, cy);

          for (let i = 0; i < 6; i++) {
            const v1 = verts[i];
            const v2 = verts[(i + 1) % 6];
            let alpha = BASE_ALPHA;

            if (isNear) {
              const dist = distToSegment(
                mouse.x,
                mouse.y,
                v1.x,
                v1.y,
                v2.x,
                v2.y
              );
              if (dist < GLOW_RADIUS) {
                const t = 1 - dist / GLOW_RADIUS;
                const ease = t * t * (3 - 2 * t);
                alpha = BASE_ALPHA + (MAX_GLOW_ALPHA - BASE_ALPHA) * ease;
              }
            }

            ctx!.beginPath();
            ctx!.moveTo(v1.x, v1.y);
            ctx!.lineTo(v2.x, v2.y);
            ctx!.strokeStyle = `rgba(${hexColor}, ${alpha})`;
            ctx!.lineWidth = alpha > 0.15 ? 1.5 : 1;
            ctx!.stroke();
          }
        }
      }

      if (!reducedMotion) {
        updatePulses(cols, rows, now);
        drawPulses();
        animFrame = requestAnimationFrame(draw);
      }
    }

    resize();
    window.addEventListener("resize", resize);
    if (!reducedMotion) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseleave", onMouseLeave);
      document.addEventListener("click", onClick);
      document.addEventListener("touchstart", onTouchStart);
      animFrame = requestAnimationFrame(draw);
    }

    return () => {
      cancelAnimationFrame(animFrame);
      window.removeEventListener("resize", resize);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("click", onClick);
      document.removeEventListener("touchstart", onTouchStart);
    };
  }, [reducedMotion, theme.palette.hexColor]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        pointerEvents: "none",
        position: "fixed",
        inset: 0,
        zIndex: 0,
        width: "100%",
        height: "100%"
      }}
    />
  );
}
